// src/domain/models/task/taskFormatter.js
/**
 * Builds only the core segment of a task's line representation.
 * @param {Object} parsed - The parsed task data.
 * @returns {string} The core segment string.
 */
export const buildCoreSegment = (parsed) => {
  let segment = '';

  if (parsed.completionMark) {
    segment += parsed.completionMark + ' ';
  }
  if (parsed.priority) {
    segment += '!' + parsed.priority + ' ';
  }

  segment += parsed.description.trim();

  if (parsed.tags && parsed.tags.length > 0) {
    segment += ' ' + parsed.tags.join(' ');
  }

  return segment.trim();
};

/**
 * A predefined list of core attribute keys with dedicated getters and setters.
 * These attributes are integral to the Task domain model.
 * @type {string[]}
 */
const CORE_ATTRIBUTES = ['description', 'priority', 'tags', 'completionMark'];

/**
 * Builds the extended attributes block string from parsed attributes.
 * Excludes core attributes from the result.
 *
 * @param {Object} parsed - The parsed task data.
 * @returns {string} The extended attributes string or an empty string.
 */
export const buildExtendedAttributesBlock = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return '';

  const extendedAttributes = Object.keys(parsed)
    .filter((key) => !CORE_ATTRIBUTES.includes(key)) // Exclude core attributes
    .map((key) => {
      const value = parsed[key];
      return `${key}: ${typeof value === 'string' ? `"${value}"` : value}`;
    });

  return extendedAttributes.length > 0
    ? `{ ${extendedAttributes.join(', ')} }`
    : '';
};

/**
 * @fileoverview Provides formatting utilities to build and extract
 * line representations from a parsed Task object.
 * Keeps formatting logic separate, adhering to SRP and DRY principles.
 */

/**
 * Builds the full line representation from parsed task data.
 * @param {Object} parsed - The parsed task data.
 * @returns {string} The full line representation.
 */
export const buildStringRepresentation = (parsed) => {
  const core = buildCoreSegment(parsed);
  const ext = buildExtendedAttributesBlock(parsed);
  return ext ? `${core} ${ext}`.trim() : core;
};
